import type { CSSProperties } from 'react';
import { makeStyles } from 'tss-react/mui';

import {
  SIDEKICK_DOCK_BAR_HEIGHT_PX,
  SIDEKICK_DOCK_EDGE_OFFSET_PX,
  SIDEKICK_DOCK_EXPANDED_HEIGHT_PX,
  SIDEKICK_DOCK_RADIUS_PX,
  SIDEKICK_DOCK_WIDTH_PX,
} from '../../constants';
import {
  DOCK_BAR_GAP_PX,
  DOCK_BAR_PADDING_BOTTOM_PX,
  DOCK_BAR_WIDTH_PX,
  DOCK_BORDER_GRADIENT_ANGLE,
  DOCK_BORDER_WIDTH_PX,
} from './constants';

/** Styles for the docked entry point in both of its resting sizes. */
const useStyles = makeStyles()((theme) => {
  const barRadius = theme.new.shape.borderRadius.medium;

  // A gradient cannot be a border colour, so the stroke is painted as a
  // background and masked down to the outer band. `padding` sets which
  // sides carry the stroke: a side left unpadded draws none.
  const gradientBorder = (
    padding: string,
    stops: ReadonlyArray<CSSProperties['color']>
  ) => ({
    content: '""',
    position: 'absolute' as const,
    inset: 0,
    borderRadius: 'inherit',
    padding,
    background: `linear-gradient(${DOCK_BORDER_GRADIENT_ANGLE}, ${stops.map(String).join(', ')})`,
    WebkitMask:
      'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
    WebkitMaskComposite: 'xor',
    maskComposite: 'exclude',
    pointerEvents: 'none' as const,
  });

  return {
    root: {
      position: 'fixed',
      right: SIDEKICK_DOCK_EDGE_OFFSET_PX,
      // Both resting sizes are fixed widths drawn for desktop, so a
      // viewport narrower than the card would push its left edge off
      // screen. The cap keeps the same gap on both sides instead.
      maxWidth: `calc(100vw - ${SIDEKICK_DOCK_EDGE_OFFSET_PX * 2}px)`,
      // The dock shares the bottom-right corner with the host's
      // snackbar stack, which reserves room for whichever resting size
      // is showing. Sitting one step above `snackbar` keeps the
      // assistant on top if a host ever stacks something there
      // unreserved.
      zIndex: theme.zIndex.snackbar + 1,
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: theme.new.color.common.white,
    },
    bar: {
      // The bar tucks into the viewport's bottom edge, so it is
      // rounded and bordered on three sides only. The card it expands
      // into floats clear of the edge instead.
      bottom: 0,
      width: DOCK_BAR_WIDTH_PX,
      height: SIDEKICK_DOCK_BAR_HEIGHT_PX,
      borderRadius: `${barRadius} ${barRadius} 0 0`,
      flexDirection: 'row',
      alignItems: 'center',
      gap: DOCK_BAR_GAP_PX,
      padding: `${theme.new.spacing.medium} ${theme.new.spacing.large} ${DOCK_BAR_PADDING_BOTTOM_PX}px`,
      // The bottom side is left unpadded so no stroke lands on the
      // tucked edge.
      '&::before': gradientBorder(
        `${DOCK_BORDER_WIDTH_PX}px ${DOCK_BORDER_WIDTH_PX}px 0`,
        [
          theme.new.color.brand[300],
          theme.new.color.brand[600],
          theme.new.color.purple[600],
        ]
      ),
      '&:hover': {
        backgroundColor: theme.new.color.brand[100],
      },
      '&:active': {
        backgroundColor: theme.new.color.brand[200],
      },
    },
    // The bar's glyph is decorative, so unlike the card's minimize
    // control it carries no button chrome and no slot larger than the
    // glyph itself, which would outgrow the bar's fixed height.
    barGlyph: {
      display: 'flex',
      flexShrink: 0,
      color: theme.new.color.textSecondary,
    },
    barLabel: {
      ...theme.typography.body_medium,
      color: theme.new.color.textPrimary,
      // The bar is a fixed width, so a label longer than its slot
      // truncates rather than growing the bar past the design height.
      flex: 1,
      minWidth: 0,
      textAlign: 'left',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    card: {
      bottom: SIDEKICK_DOCK_EDGE_OFFSET_PX,
      borderRadius: SIDEKICK_DOCK_RADIUS_PX,
      boxShadow: theme.new.boxShadows.elevation2,
      width: SIDEKICK_DOCK_WIDTH_PX,
      // The card is a floating surface, so it must never outgrow the
      // viewport on short screens; the body scrolls instead.
      maxHeight: `calc(100vh - ${SIDEKICK_DOCK_EDGE_OFFSET_PX * 2}px)`,
      height: SIDEKICK_DOCK_EXPANDED_HEIGHT_PX,
      padding: theme.new.spacing.large,
      gap: theme.new.spacing.medium,
      // Unlike the bar, the card's stroke runs straight from brand to
      // purple with no lighter lead-in.
      '&::before': gradientBorder(`${DOCK_BORDER_WIDTH_PX}px`, [
        theme.new.color.brand[600],
        theme.new.color.purple[600],
      ]),
    },
    cardHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexShrink: 0,
    },
    cardBody: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      flex: 1,
      minHeight: 0,
      gap: theme.new.spacing.small,
      overflowY: 'auto',
    },
    title: {
      ...theme.typography.header1_medium,
      color: theme.new.color.textPrimary,
      textAlign: 'center',
    },
    subtitle: {
      ...theme.typography.small_body_regular,
      color: theme.new.color.textSecondary,
      textAlign: 'center',
    },
    chipsColumn: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%',
      gap: theme.new.spacing.small,
      marginTop: theme.new.spacing.small,
    },
    cardFooter: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.new.spacing.small,
      flexShrink: 0,
    },
    disclaimer: {
      ...theme.typography.caption_regular,
      color: theme.new.color.textSecondary,
      textAlign: 'center',
    },
    iconSlot: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      width: 32,
      height: 32,
      padding: 0,
      border: 0,
      borderRadius: '50%',
      backgroundColor: 'transparent',
      color: theme.new.color.textSecondary,
      cursor: 'pointer',
      '&:focus-visible': {
        outline: `2px solid ${theme.new.color.brand[600]}`,
        outlineOffset: 2,
      },
    },
    upsellBody: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      flex: 1,
      gap: theme.new.spacing.medium,
      paddingBlock: theme.new.spacing.medium,
      textAlign: 'center',
    },
    upsellMessage: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: theme.new.spacing.small,
      width: '100%',
      opacity: 0.8,
    },
    upsellIcon: {
      flexShrink: 0,
      color: theme.new.color.textPrimary,
    },
    upsellCopy: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: theme.new.spacing.xsmall,
      width: '100%',
    },
  };
});

export default useStyles;
